import {
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CdkDrag } from "@angular/cdk/drag-drop";

@Component({
  selector: "app-floating-progress-dialog",
  templateUrl: "./floating-progress-dialog.component.html",
  styleUrl: "./floating-progress-dialog.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloatingProgressDialogComponent implements OnChanges, OnDestroy {
  private static readonly AUTO_MINIMIZE_DELAY_MS = 2500;
  @ViewChild(CdkDrag) private drag?: CdkDrag;
  private autoMinimizeTimer?: ReturnType<typeof setTimeout>;
  @Input() progressTitle: string = "Progress";
  @Input() progressLabel: string = "Overall progress";
  @Input() progress: number = 0;
  @Input() progressText: string = "";
  @Input() progressSubtext: string = "";
  @Input() progressRun: number = 0;
  @Input() startExpanded: boolean = false;
  @Input() autoMinimize: boolean = false;
  @Output() closeDialog = new EventEmitter<void>();
  isMinimized: boolean = false;

  constructor(private changeDetectorRef?: ChangeDetectorRef) {}

  get progressValue(): number {
    return Math.min(100, Math.max(0, this.progress));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["progressRun"]) {
      this.clearAutoMinimizeTimer();
      this.isMinimized = !this.startExpanded;
      if (this.isMinimized) {
        requestAnimationFrame(() => this.drag?.reset());
      }
    }

    if (
      changes["progress"] ||
      changes["progressSubtext"] ||
      changes["autoMinimize"]
    ) {
      this.scheduleAutoMinimizeIfComplete();
    }
  }

  minimize(): void {
    this.isMinimized = true;
    requestAnimationFrame(() => this.drag?.reset());
  }

  maximize(): void {
    this.isMinimized = false;
  }

  ngOnDestroy(): void {
    this.clearAutoMinimizeTimer();
  }

  private scheduleAutoMinimizeIfComplete(): void {
    this.clearAutoMinimizeTimer();
    if (
      this.isMinimized ||
      !this.autoMinimize ||
      this.progressValue < 100 ||
      this.progressSubtext
    ) {
      return;
    }

    this.autoMinimizeTimer = setTimeout(() => {
      this.autoMinimizeTimer = undefined;
      this.minimize();
      this.changeDetectorRef?.markForCheck();
    }, FloatingProgressDialogComponent.AUTO_MINIMIZE_DELAY_MS);
  }

  private clearAutoMinimizeTimer(): void {
    if (this.autoMinimizeTimer) {
      clearTimeout(this.autoMinimizeTimer);
      this.autoMinimizeTimer = undefined;
    }
  }
}